import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Button, ToastContainer } from './ui';
import { PlusIcon } from '@heroicons/react/24/outline';
import { safeErrorMessage } from '../utils/apiError';

function ExchangeRates({ token }) {
  const [rates, setRates] = useState([]);
  const [currentRate, setCurrentRate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toasts, setToasts] = useState([]);
  const [formData, setFormData] = useState({ rate: '', effective_date: '', notes: '' });

  const addToast = (message, type = 'success') => setToasts(prev => [...prev, { message, type }]);
  const removeToast = (index) => setToasts(prev => prev.filter((_, i) => i !== index));

  const fetchRates = useCallback(async () => {
    try {
      const [ratesRes, currentRes] = await Promise.all([
        fetch('/api/v1/exchange-rates', { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch('/api/v1/exchange-rates/current', { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      setRates(await ratesRes.json());
      setCurrentRate(await currentRes.json());
    } catch (error) {
      console.error('Error fetching rates:', error);
    }
  }, [token]);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await fetch('/api/v1/exchange-rates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setShowForm(false);
        fetchRates();
        setFormData({ rate: '', effective_date: '', notes: '' });
      } else {
        addToast(await safeErrorMessage(response, 'Failed to set exchange rate'), 'error');
      }
    } catch (error) {
      console.error('Error:', error);
      addToast('Network error. Please check your connection and try again.', 'error');
    }
  };

  const columns = [
    { header: 'Date', render: (row) => new Date(row.effective_date).toLocaleDateString() },
    { header: 'Rate (USD → ZWG)', render: (row) => <strong>{row.rate}</strong> },
    { header: 'Notes', render: (row) => row.notes || '—' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--color-text)' }}>Exchange Rate Management</h2>
        <Button variant="primary" size="sm" onClick={() => setShowForm(!showForm)}>
          <PlusIcon style={{ width: 16, height: 16 }} />
          {showForm ? 'Cancel' : 'Set Rate'}
        </Button>
      </div>

      {currentRate && (
        <div style={{
          background: 'linear-gradient(135deg, var(--color-primary) 0%, #1E3A8A 100%)',
          borderRadius: 'var(--radius-lg)', padding: '1.5rem', color: 'white', marginBottom: '1.5rem'
        }}>
          <div style={{ fontSize: '0.85rem', opacity: 0.8, marginBottom: '0.5rem' }}>Current Exchange Rate</div>
          <div style={{ fontSize: '2rem', fontWeight: 700 }}>1 USD = {currentRate.rate} ZWG</div>
          <div style={{ fontSize: '0.8rem', opacity: 0.7, marginTop: '0.25rem' }}>
            {currentRate.effective_date
              ? `Effective: ${new Date(currentRate.effective_date).toLocaleDateString()}`
              : 'No rate has been set yet — showing default 1:1 rate'}
            {currentRate.notes && ` — ${currentRate.notes}`}
          </div>
        </div>
      )}

      {showForm && (
        <Card>
          <h3 style={{ marginBottom: '1rem', fontSize: '1rem', fontWeight: 600 }}>Set New Exchange Rate</h3>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Exchange Rate *</label>
                <input type="number" step="0.001" value={formData.rate} onChange={(e) => setFormData({...formData, rate: e.target.value})} placeholder="e.g., 0.035" required />
                <small style={{ color: 'var(--color-text-muted)' }}>1 ZWG = ? USD</small>
              </div>
              <div className="form-group">
                <label>Effective Date *</label>
                <input type="date" value={formData.effective_date} onChange={(e) => setFormData({...formData, effective_date: e.target.value})} required />
              </div>
            </div>
            <div className="form-group">
              <label>Notes</label>
              <textarea value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} rows="2" placeholder="Reason for rate change" />
            </div>
            <Button type="submit" variant="primary">Set Rate</Button>
          </form>
        </Card>
      )}

      <Card title="Rate History">
        <Table columns={columns} data={rates} emptyMessage="No exchange rates recorded yet." />
      </Card>

      <ToastContainer toasts={toasts} onClose={removeToast} />
    </div>
  );
}

export default ExchangeRates;
